import React, { useState, useRef, useEffect } from "react";
import MessageBox from "../MessageBox";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";

const FormDropdown = ({
  label,
  options = [],
  selected,
  onSelect,
  placeholder,
  error,
  required = false,
  labelAbove = false,
  disabled = false,
  smaller = false,
  suffix = "",
  showRequiredText = false, // Toggle required text instead of "*"
  boldLabel = false, // Toggle bold label
  showIcons = false, // New prop to enable icon display
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation("global");
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0, top: 0 });
  const dropdownRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const selectedOptionRef = useRef(null);

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || options.length === 0) return;

    const buttonRect = dropdownRef.current.getBoundingClientRect();
    setDropdownPosition({
      // left: buttonRect.left,
      // top: buttonRect.bottom + window.scrollY, // Position below the button
    });

    setIsOpen((prev) => !prev);
  };

  const handleOptionClick = (value) => {
    if (disabled) return;
    onSelect(value);
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen && selectedOptionRef.current && dropdownMenuRef.current) {
      const menu = dropdownMenuRef.current;
      const selectedOption = selectedOptionRef.current;

      const optionTop = selectedOption.offsetTop;
      const optionHeight = selectedOption.offsetHeight;
      const menuScrollTop = menu.scrollTop;
      const menuHeight = menu.clientHeight;

      const scrollTo = optionTop + optionHeight / 2 - menuHeight / 2;
      menu.scrollTo({
        top: scrollTo,
        behavior: "auto",
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === selected);
  
  // Check if any option has an icon
  const hasIcons = options.some(opt => opt.icon);
  const shouldShowIcons = showIcons && hasIcons;
  
  const selectedLabel = selectedOption
    ? `${selectedOption.label}${suffix ? ` ${suffix}` : ""}`
    : placeholder;

  // Render option with icon if available
  const renderOptionLabel = (option) => {
    const Icon = option.icon;
    return (
      <div className={styles.optionContent}>
        {shouldShowIcons && Icon && (
          <span className={styles.optionIcon}>
            <Icon />
          </span>
        )}
        <span className={styles.optionLabel}>
          {option.label}
          {suffix && ` ${suffix}`}
        </span>
      </div>
    );
  };

  // Render selected value with icon if available
  const renderSelectedLabel = () => {
    if (!selectedOption) return selectedLabel;
    
    const Icon = selectedOption.icon;
    return (
      <div className={styles.selectedContent}>
        {shouldShowIcons && Icon && (
          <span className={styles.selectedIcon}>
            <Icon />
          </span>
        )}
        <span className={styles.selectedLabel}>
          {selectedOption.label}
          {suffix && ` ${suffix}`}
        </span>
      </div>
    );
  };

  return (
    <div
      className={`${styles.dropdownWrapper} ${
        labelAbove ? styles.labelAbove : styles.labelInline
      } ${disabled ? styles.disabledWrapper : ""} ${
        smaller ? styles.smaller : ""
      }`}
    >
      {label && (
        <p
          className={styles.dropdownLabel}
          style={{
            fontWeight: boldLabel ? "600" : "normal",
            fontSize: boldLabel ? "16px" : "14px",
          }}
        >
          {label}
          {required && (
            <span
              className={styles.requiredIndicator}
              style={{ fontSize: showRequiredText ? "inherit" : "18px" }}
            >
              {showRequiredText
                ? ` (${t("provide.step2.pictures.required")} *)`
                : `(*)`}
            </span>
          )}
        </p>
      )}
      <div className={styles.dropdownAndErrWrapper}>
        <div
          className={`${styles.dropdown} ${
            disabled ? styles.disabledDropdown : ""
          }`}
          ref={dropdownRef}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`${styles.dropdownButton} ${
              disabled ? styles.disabledButton : ""
            } ${shouldShowIcons ? styles.hasIcons : ""}`}
            onClick={toggleDropdown}
            // aria-haspopup="listbox"
            // aria-expanded={isOpen}
            disabled={disabled || options.length === 0}
          >
            {renderSelectedLabel()}
            <>
              <span
                className={`${styles.arrow} ${
                  false ? styles.arrowUp : styles.arrowDown
                }`}
              />
            </>
          </div>
          {isOpen && options.length > 0 && !disabled && (
            <>
              <div
                className={styles.dropdownBackdrop}
                onClick={() => setIsOpen(false)}
              />
              <div className={styles.dropdownMenuContainer}>
                <ul
                  className={styles.dropdownMenu}
                  role="listbox"
                  ref={dropdownMenuRef}
                  style={{
                    left: dropdownPosition.left,
                    top: dropdownPosition.top,
                  }}
                >
                  {(label || placeholder) && (
                    <li className={styles.dropdownMenuLabel}>
                      {label || placeholder}
                    </li>
                  )}

                  {options.map((option) => (
                    <li
                      key={option.value}
                      className={`${styles.dropdownOption} ${
                        option.value === selected ? styles.selectedOption : ""
                      } ${shouldShowIcons ? styles.hasIcons : ""}`}
                      role="option"
                      aria-selected={option.value === selected}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOptionClick(option.value);
                      }}
                      ref={option.value === selected ? selectedOptionRef : null}
                      tabIndex={0}
                    >
                      {renderOptionLabel(option)}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
        {error && <MessageBox type="error" message={error} />}
      </div>
    </div>
  );
};

export default FormDropdown;